import { useEffect, useState } from 'react';
import { ArrowLeft, EyeOff, Shield, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { MapleMarketApp } from '@/data/mapleMarketApps';
import type {
  MarketplaceMemberRole,
  MarketplaceTeamMember,
} from '@/hooks/supabase/useMarketplaceMemberships';
import BasicTab from '@/components/maple-market/admin/tabs/BasicTab';
import DescriptionTab from '@/components/maple-market/admin/tabs/DescriptionTab';
import RatingsTab from '@/components/maple-market/admin/tabs/RatingsTab';
import AssetsTab from '@/components/maple-market/admin/tabs/AssetsTab';
import SettingsTab from '@/components/maple-market/admin/tabs/SettingsTab';
import PublishTab from '@/components/maple-market/admin/tabs/PublishTab';
import TeamTab from '@/components/maple-market/admin/tabs/TeamTab';
import KeysPanel from '@/components/maple-market/admin/KeysPanel';

export type AppDetailTab = 'basic' | 'description' | 'ratings' | 'assets' | 'settings' | 'publish' | 'keys' | 'team';

interface AppDetailPanelProps {
  product: MapleMarketApp;
  initialTab?: AppDetailTab;
  roleLabel: string;
  canManageProduct: boolean;
  canManageTeam: boolean;
  isSiteAdmin: boolean;
  onBack: () => void;
  onSaved: () => Promise<void>;
  listMembers: (productId: string) => Promise<MarketplaceTeamMember[]>;
  addMemberByEmail: (productId: string, emailOrUserId: string, role: MarketplaceMemberRole) => Promise<void>;
  updateMemberRole: (membershipId: string, role: MarketplaceMemberRole) => Promise<void>;
  removeMember: (membershipId: string) => Promise<void>;
}

const tabConfig: Array<{ value: AppDetailTab; label: string }> = [
  { value: 'basic', label: 'Basic info' },
  { value: 'description', label: 'Description' },
  { value: 'ratings', label: 'Ratings' },
  { value: 'assets', label: 'Assets' },
  { value: 'settings', label: 'Settings' },
  { value: 'publish', label: 'Publishing' },
  { value: 'keys', label: 'Keys' },
  { value: 'team', label: 'Team' },
];

function getVisibilityBadgeVariant(status: MapleMarketApp['visibilityStatus']) {
  return status === 'published' ? 'default' : 'secondary';
}

export default function AppDetailPanel({
  product,
  initialTab = 'basic',
  roleLabel,
  canManageProduct,
  canManageTeam,
  isSiteAdmin,
  onBack,
  onSaved,
  listMembers,
  addMemberByEmail,
  updateMemberRole,
  removeMember,
}: AppDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<AppDetailTab>(initialTab);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, product.id]);

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <Button type="button" variant="ghost" className="w-fit gap-2 px-0 text-muted-foreground" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            Back to applications
          </Button>
          <div className="flex items-start gap-4">
            <img src={product.image} alt={product.name} className="h-16 w-16 rounded-xl object-cover" />
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground">{product.name}</h1>
                <Badge variant={canManageProduct ? 'default' : 'secondary'} className="capitalize">
                  {canManageProduct ? 'editable' : 'read only'}
                </Badge>
                <Badge variant="outline" className="capitalize">
                  {roleLabel}
                </Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {product.developer} · {product.category} · SKU {product.sku}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle>Product editor</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as AppDetailTab)} className="space-y-6">
              <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
                {tabConfig.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="rounded-lg border border-border bg-background px-3 py-1.5 data-[state=active]:border-primary data-[state=active]:bg-primary/10"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="basic">
                <BasicTab product={product} disabled={!canManageProduct} onSaved={onSaved} />
              </TabsContent>
              <TabsContent value="description">
                <DescriptionTab product={product} disabled={!canManageProduct} onSaved={onSaved} />
              </TabsContent>
              <TabsContent value="ratings">
                <RatingsTab product={product} />
              </TabsContent>
              <TabsContent value="assets">
                <AssetsTab product={product} disabled={!canManageProduct} onSaved={onSaved} />
              </TabsContent>
              <TabsContent value="settings">
                <SettingsTab product={product} disabled={!canManageProduct} onSaved={onSaved} />
              </TabsContent>
              <TabsContent value="publish">
                <PublishTab product={product} disabled={!canManageProduct} isSiteAdmin={isSiteAdmin} onSaved={onSaved} />
              </TabsContent>
              <TabsContent value="keys">
                <KeysPanel product={product} />
              </TabsContent>
              <TabsContent value="team">
                <TeamTab
                  product={product}
                  canManageTeam={canManageTeam}
                  listMembers={listMembers}
                  addMemberByEmail={addMemberByEmail}
                  updateMemberRole={updateMemberRole}
                  removeMember={removeMember}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Publishing summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={getVisibilityBadgeVariant(product.visibilityStatus)}>{product.visibilityStatus}</Badge>
                <Badge variant="outline">{product.publishRequestStatus}</Badge>
                {product.hasBeenPublished ? <Badge variant="secondary">Previously approved</Badge> : <Badge variant="secondary">First publish pending</Badge>}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Visibility</span>
                <span className="font-medium text-foreground">{product.visibilityStatus}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Approval</span>
                <span className="font-medium text-foreground">{product.publishRequestStatus}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Requested</span>
                <span className="font-medium text-foreground">{product.publishRequestedAt ? new Date(product.publishRequestedAt).toLocaleString() : 'Not requested'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">First published</span>
                <span className="font-medium text-foreground">{product.firstPublishedAt ? new Date(product.firstPublishedAt).toLocaleDateString() : 'Never'}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Marketplace status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Price</span>
                <span className="font-medium text-foreground">{product.isFree ? 'Free' : `$${(product.priceCents / 100).toFixed(2)}`}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Featured</span>
                <span className="font-medium text-foreground">{product.featured ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Version</span>
                <span className="font-medium text-foreground">{product.version}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Package</span>
                <span className="font-medium text-foreground">{product.packageType === 'package' ? '.mpak package' : product.packageType === 'bundle' ? 'HTML bundle' : 'Not set'}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Ratings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-amber-500">
                <Star className="h-4 w-4 fill-current" />
                <span className="font-medium text-foreground">{product.rating.toFixed(1)}</span>
              </div>
              <div className="text-muted-foreground">{product.reviews.toLocaleString()} lifetime reviews</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Permission scope</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <Shield className="mt-0.5 h-4 w-4 text-primary" />
                <p>
                  {canManageProduct
                    ? 'You can edit product metadata, package routing, visibility workflow, and Maple Keys in this panel.'
                    : 'Your current role is read-only for this product.'}
                </p>
              </div>
              <div className="flex items-start gap-2">
                <EyeOff className="mt-0.5 h-4 w-4 text-primary" />
                <p>
                  Hidden and unpublished products remain accessible here for admins, team members, and existing owners.
                </p>
              </div>
              <div className="flex items-start gap-2">
                <Shield className="mt-0.5 h-4 w-4 text-primary" />
                <p>
                  {canManageTeam
                    ? 'You can add members, change roles, and remove access.'
                    : 'Team membership is visible, but changes are restricted to owners and admins.'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}